import type { FC } from 'react';
import React, { useMemo } from 'react';
import { Divider, Stack } from '@mui/material';

import type { SurveyListItem } from 'src/types/survey';
import useIntersect from 'src/hooks/useIntersect';
import { useSurveyList } from 'src/components/survey/SurveyContext/ListContext';
import WidgetFooter from 'src/components/survey/SurveySelectionPage/components/SurveyWidgetView/WidgetTile/components/WidgetFooter';
import WidgetSkeleton from 'src/components/survey/SurveySelectionPage/components/SurveyWidgetView/WidgetTile/components/WidgetSkeleton';
import WidgetTileHeader from 'src/components/survey/SurveySelectionPage/components/SurveyWidgetView/WidgetTile/components/WidgetTileHeader';
import WidgetWrapper from 'src/components/survey/SurveySelectionPage/components/SurveyWidgetView/WidgetTile/components/WidgetWrapper';
import useWidgetTileData from 'src/components/survey/SurveySelectionPage/components/SurveyWidgetView/WidgetTile/hooks/useWidgetTileData';
import useDashboardResults from 'src/components/survey/SurveySelectionPage/hooks/useDashboardResults';

import { styles } from './index.styles';

interface WidgetTileProps {
  survey: SurveyListItem;
  enabled: boolean;
}

const WidgetTile: FC<WidgetTileProps> = ({ survey, enabled }) => {
  const [isVisible, ref] = useIntersect();
  const { data: dashboardResults, isFetching } = useDashboardResults(
    survey.id,
    enabled && isVisible,
  );

  const widgetTileData = useWidgetTileData(survey, dashboardResults);

  // Use widgetTileData for loading, error, and data
  const loading = useMemo(() => {
    if (!dashboardResults) return true;
    return isFetching || widgetTileData.isFetching;
  }, [dashboardResults, isFetching, widgetTileData.isFetching]);

  const { selectSurvey } = useSurveyList();
  const handleOpenSurvey = () => selectSurvey(survey.id);

  return (
    <Stack direction="column" sx={styles.tile} ref={ref}>
      <WidgetTileHeader
        orgUnit={dashboardResults?.user_root_org_unit}
        survey={survey}
      />
      <Stack sx={styles.body} onClick={handleOpenSurvey}>
        {loading ? (
          <WidgetSkeleton />
        ) : (
          <>
            <WidgetWrapper
              data={widgetTileData.data}
              errorMsg={widgetTileData.errorMsg}
              widgetConfig={dashboardResults?.widget}
              comparisonData={widgetTileData.comparisonData}
            />
            <Divider orientation="horizontal" />
            <WidgetFooter responseRateData={widgetTileData.responseRateData} />
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default WidgetTile;
